import { isInMatch } from '../machine';
import { ok, reject } from '../result';
import type { DomainResult } from '../result';
import type {
  AudioOp,
  Decision,
  StartAudioObservationCommand,
  StartAudioObservationSnapshot,
} from './types';

/**
 * FLAG-1 · FLAG-2 · FLAG-4 · AUD-2 · LIVE-4 — the Observation a recording is written under, at the
 * moment it **starts** (build unit U-11).
 *
 * > *"On start tap: mint `audioBlobId`; compute `matchMinute` from the state **now**; insert the
 * > `Observation` (`kind='flag'` with `hypothesisId=null`, or `kind='voiceNote'` with the hypothesis
 * > id) in one transaction. Render the recording indicator on commit.
 * > `MediaRecorder.start(TIMESLICE_MS)`."* — design §8.2 step 1
 *
 * ── Why this is a command at all ─────────────────────────────────────────────
 *
 * It would be shorter to write the row directly. It would also put a second writer on
 * `observations`, and INV-1's *"no other mutation exists on any code path"* is a claim about all
 * paths that only the bus can discharge (§1.1). The bus is also what makes the insert and the blob
 * row **one transaction**: a crash between them would leave an Observation pointing at an
 * `audioBlobId` with no row, and the first `dataavailable` would then throw inside `appendChunk`
 * with the recording already running — audio lost with the flag still on screen.
 *
 * ── The three consequences §8.2 draws, and where each is enforced ────────────
 *
 * | §8.2's consequence | Mechanism |
 * |---|---|
 * | the minute is correct for a recording that crosses a half boundary | `matchMinute` is on the pre-built row, derived at the tap by `deriveMatchMinute`; this handler never recomputes it, and `domain/`'s lint ban on `Date` means it *could* not |
 * | the Observation is never mutated, so INV-1 needs no carve-out | there is no op in {@link AudioOp} that patches an Observation, and none in `RowOp` at all |
 * | "one transaction per Command" survives AUD-3 | finalize is not a command — it is a post-commit effect over a row that already exists |
 *
 * ── FLAG-4 is a row shape, not a branch ─────────────────────────────────────
 *
 * *"When microphone permission is denied or unavailable … creates a silent timestamped flag with
 * `audioBlob` null, labeled as silent wherever flags are listed."* A silent flag arrives here as the
 * same command with `audioBlobId: null`, and the only difference downstream is that no blob row is
 * opened. There is no separate silent-flag command and no `silent` flag field: **the absence of an
 * `audioBlobId` is the silence**, so nothing can label a flag silent while a blob exists for it, or
 * the reverse.
 */

const MODULE = 'commands/audio';

/**
 * Validates the pre-built Observation and opens its blob row.
 *
 * The row is validated rather than trusted for the same reason `handleIncrement` validates its own:
 * it crosses a boundary from the UI, and its fields decide which ledger row is written and what it
 * is attached to. A mismatch here is a *silent* mis-attribution — a voice note filed against the
 * wrong hypothesis is discovered at triage, after the match, when nothing can be done about it.
 */
export function handleStartAudioObservation(
  cmd: StartAudioObservationCommand,
  snap: StartAudioObservationSnapshot,
): Decision<AudioOp> {
  const row = cmd.observation;

  if (snap.match === undefined) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_MATCH_NOT_FOUND',
      'That match no longer exists, so there is nothing to record against.',
      { kind: row.kind },
    );
  }

  /**
   * §6.4a's negative space, for the Flag bar.
   *
   * *"Taps on tiles or rows before kickoff create no Observation."* The Flag bar is part of the §5.6
   * chrome and is therefore present on a capture surface the user reaches *before* Kickoff, so this
   * is a reachable state and not a defensive check. Read from the snapshot rather than from
   * whatever the surface last rendered, so a Full Time that commits between the finger landing and
   * this transaction opening closes the bar too.
   */
  if (!isInMatch(snap.match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_MATCH_NOT_CAPTURING',
      'The match is not under way, so there is no minute to stamp this recording with.',
      { kind: row.kind, state: snap.match.state },
    );
  }

  if (row.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'The pre-built Observation names a different match than the command does.',
      { rowMatchId: row.matchId, kind: row.kind },
    );
  }

  /**
   * `increment` is refused here, and that is INV-4's audio face.
   *
   * An `Increment` reaches {@link CaptureOp}, which carries `writeCountState`; this command reaches
   * {@link AudioOp}, which does not. Letting an `increment` row through *this* command would be a
   * way to insert a counted observation that never updates the count — INV-2 drift written by the
   * app itself, which §13.6's alarm would then report at every transition for the rest of the match.
   */
  if (row.kind !== 'flag' && row.kind !== 'voiceNote') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'Only a flag or a voice note is started this way; an increment is dispatched as an Increment.',
      { kind: row.kind },
    );
  }

  const attachment = checkAttachment(cmd, snap, row.kind, row.hypothesisId);
  if (!attachment.ok) return attachment;

  return ok({
    ops: [
      { t: 'insertObservation', row },
      // FLAG-4 — a silent flag opens no blob row. The absence of the id *is* the silence.
      ...(row.audioBlobId === null
        ? []
        : ([{ t: 'openAudioBlob', id: row.audioBlobId, createdAt: row.createdAt }] as const)),
    ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg:
          row.audioBlobId === null
            ? 'FLAG-4: a silent flag was filed — the microphone was unavailable'
            : `${row.kind} started`,
        data: {
          observationId: row.id,
          kind: row.kind,
          matchMinute: row.matchMinute,
          hypothesisId: row.hypothesisId,
          audioBlobId: row.audioBlobId,
        },
      },
    ],
    post: [],
  });
}

/**
 * FLAG-2 and AUD-2, which are the same rule read from opposite ends.
 *
 * FLAG-2: *"Flags are Observations with `kind=flag` and `hypothesisId=null`, queued for triage."*
 * AUD-2: *"Voice notes attach to a hypothesis."* So the kind determines whether an attachment is
 * required *or forbidden*, and both directions are checked — a flag carrying a hypothesis id would
 * quietly leave DIST-1's triage queue (which is defined over unattached flags), and a voice note
 * without one would never appear against any hypothesis at halftime or in distillation. Neither
 * failure is visible until after the match.
 */
function checkAttachment(
  cmd: StartAudioObservationCommand,
  snap: StartAudioObservationSnapshot,
  kind: 'flag' | 'voiceNote',
  hypothesisId: string | null,
): DomainResult<null> {
  if (kind === 'flag') {
    if (hypothesisId !== null) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_COMMAND_ROW_MISMATCH',
        'FLAG-2: a flag carries no hypothesis — it is filed unattached and resolved at triage.',
        { hypothesisId },
      );
    }
    return ok(null);
  }

  if (hypothesisId === null) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'AUD-2: a voice note attaches to a hypothesis, and this one names none.',
      {},
    );
  }
  if (snap.hypothesis === undefined) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_HYPOTHESIS_NOT_FOUND',
      'That hypothesis no longer exists, so the note would have nothing to attach to.',
      { hypothesisId },
    );
  }
  if (snap.hypothesis.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That hypothesis belongs to a different match.',
      { hypothesisId, rowMatchId: snap.hypothesis.matchId },
    );
  }
  return ok(null);
}
